import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MENSAJE_MENU_VACIO } from '@/app/core/access/acceso-mensajes';
import { SafeHtmlPipe } from '@/app/shared/ui/pipes/safe-html.pipe';
import { NavIcons } from '@/app/shared/nav/nav-icons';
import { NavItem, NavSection } from '@/app/shared/nav/nav-section.model';
import { ShellStore } from './shell.store';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="sidebar" [class.collapsed]="collapsed()" aria-label="Navegación principal">
      <!-- Spacer que alinea el sidebar con el header principal -->
      <div class="sidebar-logo">
        @if (!collapsed()) {
          <div>
            <strong>Colmena</strong>
            <p class="sidebar-logo__caption">Consola comercial</p>
          </div>
        }
      </div>

      <!-- Drill-in header: aparece al navegar dentro de un sub-menú -->
      @if (!collapsed() && store.drilledItem() !== null) {
        <div class="drill-header">
          <button
            class="back-btn"
            (click)="store.drillOut()"
            aria-label="Volver al menú principal"
          >
            <span
              class="back-icon"
              [innerHTML]="icons.chevronLeft | safeHtml"
              aria-hidden="true"
            ></span>
            Menú principal
          </button>
          <span class="drill-title">{{
            store.drilledItem()?.drillTitle ?? store.drilledItem()?.label
          }}</span>
        </div>
      }

      <!-- Navigation body -->
      <div class="nav-body">
        @if (store.drilledItem() === null) {
          <!-- ── MENÚ PRINCIPAL ── -->
          <div
            class="nav-menu"
            [class.nav-slide-out]="store.animDir() === 'out'"
          >
            @for (section of sections(); track section.title) {
              <div class="nav-section">
                @if (!collapsed()) {
                  <span class="section-label">{{ section.title }}</span>
                }
                @for (item of section.items; track item.label) {
                  @if (item.children?.length) {
                    <button
                      class="nav-item"
                      [class.active]="store.isItemActive(item)"
                      (click)="onDrillIn(item)"
                      [title]="item.label"
                      [attr.aria-label]="collapsed() ? item.label : null"
                      (focus)="mostrarEtiqueta($event, item.label)"
                      (blur)="ocultarEtiqueta()"
                      aria-haspopup="true"
                    >
                      @if (item.icon) {
                        <span
                          class="nav-icon"
                          [innerHTML]="item.icon | safeHtml"
                          aria-hidden="true"
                        ></span>
                      } @else {
                        <span class="nav-dot" aria-hidden="true"></span>
                      }
                      @if (!collapsed()) {
                        <span class="nav-label">{{ item.label }}</span>
                        <span
                          class="nav-chevron"
                          [innerHTML]="icons.chevronRight | safeHtml"
                          aria-hidden="true"
                        ></span>
                      }
                    </button>
                  } @else {
                    <a
                      class="nav-item"
                      [routerLink]="item.route"
                      (click)="onNavigate()"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="{ exact: true }"
                      [title]="item.label"
                      [attr.aria-label]="collapsed() ? item.label : null"
                      (focus)="mostrarEtiqueta($event, item.label)"
                      (blur)="ocultarEtiqueta()"
                    >
                      @if (item.icon) {
                        <span
                          class="nav-icon"
                          [innerHTML]="item.icon | safeHtml"
                          aria-hidden="true"
                        ></span>
                      } @else {
                        <span class="nav-dot" aria-hidden="true"></span>
                      }
                      @if (!collapsed()) {
                        <span class="nav-label">{{ item.label }}</span>
                      }
                    </a>
                  }
                }
              </div>
            }

            @if (!sections().length) {
              <p class="nav-empty">{{ textoMenuVacio() }}</p>
            }
          </div>
        } @else {
          <!-- ── SUB-MENÚ ── -->
          <div class="nav-menu" [class.nav-slide-in]="store.animDir() === 'in'">
            @for (
              item of store.drilledItem()!.children ?? [];
              track item.label
            ) {
              @if (item.children?.length) {
                <button
                  class="nav-item"
                  [class.active]="store.isItemActive(item)"
                  (click)="onDrillIn(item)"
                  [title]="item.label"
                  [attr.aria-label]="collapsed() ? item.label : null"
                  (focus)="mostrarEtiqueta($event, item.label)"
                  (blur)="ocultarEtiqueta()"
                  aria-haspopup="true"
                >
                  @if (item.icon) {
                    <span
                      class="nav-icon"
                      [innerHTML]="item.icon | safeHtml"
                      aria-hidden="true"
                    ></span>
                  } @else {
                    <span class="nav-dot" aria-hidden="true"></span>
                  }
                  @if (!collapsed()) {
                    <span class="nav-label">{{ item.label }}</span>
                    <span
                      class="nav-chevron"
                      [innerHTML]="icons.chevronRight | safeHtml"
                      aria-hidden="true"
                    ></span>
                  }
                </button>
              } @else {
                <a
                  class="nav-item"
                  [routerLink]="item.route"
                  (click)="onNavigate()"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: true }"
                  [title]="item.label"
                  [attr.aria-label]="collapsed() ? item.label : null"
                  (focus)="mostrarEtiqueta($event, item.label)"
                  (blur)="ocultarEtiqueta()"
                >
                  @if (item.icon) {
                    <span
                      class="nav-icon"
                      [innerHTML]="item.icon | safeHtml"
                      aria-hidden="true"
                    ></span>
                  } @else {
                    <span class="nav-dot" aria-hidden="true"></span>
                  }
                  @if (!collapsed()) {
                    <span class="nav-label">{{ item.label }}</span>
                  }
                </a>
              }
            }
          </div>
        }
      </div>
    </nav>
    @if (collapsed() && etiquetaFoco(); as e) {
      <span class="nav-etiqueta-foco" aria-hidden="true" [style.top.px]="e.top">{{ e.texto }}</span>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        min-height: 0;
        height: 100%;
      }

      /* ── Shell ──────────────────────────────────────────── */
      .sidebar {
        background: var(--surface);
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        transition: width 200ms ease;
      }

      .sidebar-logo {
        display: none;
        flex-shrink: 0;
      }

      .sidebar-logo__caption {
        margin: 0;
        color: var(--muted);
        font-size: var(--t-caption);
      }

      /* ── Drill header ───────────────────────────────────── */
      .drill-header {
        padding: var(--shell-menu-padding-inline) var(--shell-menu-padding-inline) 0;
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
      }
      .back-btn {
        display: flex;
        align-items: center;
        gap: calc(var(--esp-interno) / 4);
        background: none;
        border: none;
        cursor: pointer;
        font-family: var(--font);
        font-size: var(--shell-meta-font-size);
        font-weight: 500;
        color: var(--muted);
        padding: calc(var(--esp-interno) / 4) calc(var(--esp-interno) / 2);
        border-radius: var(--radius-xs);
        width: 100%;
        text-align: left;
        transition:
          color 120ms,
          background 120ms;
      }
      .back-btn:hover {
        color: var(--primary);
        background: var(--surface-2);
      }
      .back-icon {
        display: flex;
        align-items: center;
      }
      .back-icon ::ng-deep svg {
        width: var(--shell-menu-icon-size);
        height: var(--shell-menu-icon-size);
        stroke: currentColor;
      }
      .drill-title {
        display: block;
        font-size: var(--t-micro);
        font-weight: 500;
        color: var(--muted);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        padding: calc(var(--esp-interno) / 2) var(--shell-menu-item-padding-inline) var(--shell-menu-item-padding-block);
      }

      .nav-body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: var(--shell-menu-padding-block) var(--shell-menu-padding-inline);
        display: flex;
        flex-direction: column;
        gap: var(--shell-menu-group-gap);
      }

      .nav-menu {
        display: flex;
        flex-direction: column;
        gap: var(--shell-menu-group-gap);
      }

      .nav-section {
        display: flex;
        flex-direction: column;
      }

      .section-label {
        display: block;
        font-size: var(--t-micro);
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--muted);
        padding: 0 var(--shell-menu-item-padding-inline) calc(var(--esp-interno) / 2);
      }

      .nav-item {
        display: flex;
        align-items: center;
        gap: var(--shell-menu-item-gap);
        padding: var(--shell-menu-item-padding-block) var(--shell-menu-item-padding-inline);
        border-radius: var(--r-chico);
        color: var(--gris);
        text-decoration: none;
        font-size: var(--shell-menu-font-size);
        font-weight: 500;
        white-space: nowrap;
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        cursor: pointer;
        font-family: var(--font);
        transition:
          background 120ms,
          color 120ms;
      }
      .nav-item:hover {
        background: var(--fondo-suave);
        color: var(--enlace);
        text-decoration: none;
      }
      .nav-item.active {
        background: var(--celeste);
        color: var(--enlace);
      }
      .nav-label {
        flex: 1;
        min-width: 0;
        white-space: normal;
        overflow-wrap: anywhere;
      }

      .nav-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--shell-menu-icon-size);
        height: var(--shell-menu-icon-size);
        color: var(--gris-50);
      }
      .nav-icon ::ng-deep svg {
        width: var(--shell-menu-icon-size);
        height: var(--shell-menu-icon-size);
        stroke: currentColor;
      }

      .nav-item:hover .nav-icon,
      .nav-item.active .nav-icon {
        color: var(--primary);
      }

      .nav-dot {
        width: calc(var(--esp-interno) / 2);
        height: calc(var(--esp-interno) / 2);
        border-radius: var(--radius-pill);
        background: currentColor;
        flex-shrink: 0;
        margin-left: calc(var(--esp-interno) / 7);
        opacity: 0.4;
        transition: opacity 120ms;
      }
      .nav-item.active .nav-dot,
      .nav-item:hover .nav-dot {
        opacity: 1;
      }

      .nav-chevron {
        margin-left: auto;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        opacity: 0.35;
        transition:
          opacity 120ms,
          transform 120ms;
      }
      .nav-chevron ::ng-deep svg {
        width: var(--shell-menu-icon-size);
        height: var(--shell-menu-icon-size);
        stroke: currentColor;
      }
      .nav-item:hover .nav-chevron {
        opacity: 0.7;
        transform: translateX(2px);
      }
      .nav-item.active .nav-chevron {
        opacity: 0.8;
      }

      .nav-empty {
        font-size: var(--shell-meta-font-size);
        color: var(--muted);
        padding: var(--esp-interno) calc(var(--esp-interno) + (var(--esp-interno) / 2));
        margin: 0;
      }

      /* ── Slide animations ───────────────────────────────── */
      @keyframes slideFromRight {
        from {
          transform: translateX(18px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideFromLeft {
        from {
          transform: translateX(-18px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      .nav-slide-in {
        animation: slideFromRight 180ms ease;
      }
      .nav-slide-out {
        animation: slideFromLeft 180ms ease;
      }

      /* ── Collapsed state ────────────────────────────────── */
      .collapsed .nav-item {
        justify-content: center;
        padding: var(--shell-menu-item-padding-block);
      }
      .collapsed .section-label {
        display: none;
      }
      .collapsed .nav-section {
        gap: calc(var(--esp-interno) / 4);
      }
      .collapsed .sidebar-logo {
        justify-content: center;
        padding: 0;
      }
      .collapsed .nav-chevron {
        display: none;
      }

      .nav-etiqueta-foco {
        position: fixed;
        left: calc(var(--sidebar-w-collapsed) + 8px);
        z-index: 150;
        padding: 6px 10px;
        border-radius: var(--radius-xs);
        background: var(--marino);
        color: var(--surface);
        font-size: var(--t-caption);
        font-weight: 500;
        white-space: nowrap;
        pointer-events: none;
      }
    `,
  ],
})
export class SidebarComponent {
  readonly collapsed = input(false);
  readonly sections = input<NavSection[]>([]);
  protected readonly store = inject(ShellStore);
  protected readonly icons = NavIcons;
  protected readonly textoMenuVacio = computed(() => MENSAJE_MENU_VACIO[this.store.estadoSesion()]);
  protected readonly etiquetaFoco = signal<{ texto: string; top: number } | null>(null);

  protected onNavigate(): void {
    if (this.store.isMobileViewport()) {
      this.store.closeSidebar();
      queueMicrotask(() => document.getElementById('contenido-principal')?.focus());
    }
  }

  protected onDrillIn(item: NavItem): void {
    this.store.drillIn(item);

    if (item.route && this.store.isMobileViewport()) {
      this.store.closeSidebar();
      queueMicrotask(() => document.getElementById('contenido-principal')?.focus());
    }
  }

  protected mostrarEtiqueta(event: FocusEvent, texto: string): void {
    if (!this.collapsed()) {
      return;
    }

    const top = (event.target as HTMLElement).getBoundingClientRect().top + 6;
    this.etiquetaFoco.set({ texto, top });
  }

  protected ocultarEtiqueta(): void {
    this.etiquetaFoco.set(null);
  }
}
